import { useState, useEffect } from 'react';
import './testimonials.css';
import { API_URL } from '../../config/api';

function Testimonials({ book }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', quote: '', book: book.slug || book.type || '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [approvedTestimonials, setApprovedTestimonials] = useState([]);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    const bookId = book.slug || book.type || '';
    fetch(`${API_URL}/testimonials/approved?book=${bookId}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then((data) => {
        if (data.success) setApprovedTestimonials(data.testimonials);
        else setFetchError(true);
      })
      .catch(() => setFetchError(true));
  }, [book]);

  const hardcoded = (book.testimonials || []).map((t) => ({ quote: t.quote, name: t.name }));
  const fetched = approvedTestimonials.map((t) => ({ quote: t.quote, name: t.name }));
  const allTestimonials = [...hardcoded, ...fetched];

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (submitError) setSubmitError(''); // clear error on typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    // Basic validation
    if (!form.name.trim()) {
      setSubmitError('Please enter your name.');
      return;
    }
    if (!form.quote.trim()) {
      setSubmitError('Please write your testimonial before submitting.');
      return;
    }
    if (form.quote.trim().length < 10) {
      setSubmitError('Your testimonial is too short — tell us a bit more!');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/testimonials/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!response.ok) throw new Error('Server error');

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        setForm({ name: '', quote: '', book: book.slug || book.type || '' });
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitError('Unable to submit right now. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSubmitted(false);
    setSubmitError('');
  };

  return (
    <section className="testimonials">
      <h2 className="testimonial-title">What People Are Saying…</h2>

      {fetchError && (
        <p className="testimonial-fetch-error">
          Couldn't load testimonials right now. Showing existing ones below.
        </p>
      )}

      <div className="testimonial-grid">
        {allTestimonials.map((item, index) => (
          <div key={index} className={`testimonial-card ${book.type || ''}`}>
            <span className="quote-mark">❝</span>
            <p className="testimonial-quote">{item.quote}</p>
            <p className="testimonial-name">— {item.name}</p>
          </div>
        ))}

        {allTestimonials.length === 0 && (
          <p className="testimonial-empty">Be the first to share your thoughts!</p>
        )}

        <div className="testimonial-card add-card" onClick={() => setShowModal(true)}>
          <span className="add-plus">+</span>
          <p className="add-label">Add your testimonial</p>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>✕</button>

            {!submitted ? (
              <>
                <span className="quote-mark modal-quote-mark">❝</span>
                <h3 className="modal-title">Share your thoughts</h3>
                <p className="modal-subtitle">Did this book resonate with you? I'd love to hear it.</p>

                <form className="testimonial-form" onSubmit={handleSubmit}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your name"
                    value={form.name}
                    onChange={handleChange}
                    className={submitError && !form.name.trim() ? 'input-error' : ''}
                  />
                  <textarea
                    name="quote"
                    placeholder="Your testimonial..."
                    rows="4"
                    value={form.quote}
                    onChange={handleChange}
                    className={submitError && !form.quote.trim() ? 'input-error' : ''}
                  />

                  {submitError && (
                    <p className="testimonial-submit-error">⚠️ {submitError}</p>
                  )}

                  <button type="submit" disabled={loading}>
                    {loading ? 'Sending...' : 'Submit testimonial'}
                  </button>
                </form>
              </>
            ) : (
              <div className="modal-success">
                <span>🌸</span>
                <h3>Thank you!</h3>
                <p>Your testimonial has been received. It will appear after review.</p>
                <button className="modal-done-btn" onClick={closeModal}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default Testimonials;